/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Typography,
  message,
} from "antd";
import { LICENSE_AGREEMENT_PARAGRAPHS } from "./licenseAgreementContent";

const { Paragraph, Text } = Typography;

function AgreementBody() {
  return (
    <>
      {LICENSE_AGREEMENT_PARAGRAPHS.map((block, idx) => {
        if (block.intro) {
          return <Paragraph key={idx}>{block.intro}</Paragraph>;
        }
        return (
          <Paragraph
            key={idx}
            style={block.last ? { marginBottom: 0 } : undefined}
          >
            <strong>{block.heading}</strong> {block.body}
          </Paragraph>
        );
      })}
    </>
  );
}

/**
 * Project contract modal — supports mandatory gate (unsigned) and read-only review (signed).
 * @param {object} contractConfig - from PROJECT_CONTRACTS
 * @param {boolean} signed - current user has signed
 * @param {boolean} mandatory - block dismiss until signed (no close / mask click)
 * @param {boolean} open - controlled visibility (gate mode)
 * @param {() => void} onOpenChange - when user closes after signing or opens via button
 * @param {(signerName: string) => Promise<void>} onSign
 * @param {boolean} signing
 * @param {boolean} showTriggerButton - "License Agreement" link when signed
 */
export default function LicenseAgreementModal({
  contractConfig,
  signed = false,
  mandatory = false,
  open: openControlled,
  onOpenChange,
  onSign,
  signing = false,
  showTriggerButton = true,
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [form] = Form.useForm();

  const open = mandatory ? Boolean(openControlled) : internalOpen;

  useEffect(() => {
    if (mandatory && openControlled) {
      form.resetFields();
    }
  }, [mandatory, openControlled, form]);

  const setOpen = (next) => {
    if (mandatory) {
      onOpenChange?.(next);
      return;
    }
    setInternalOpen(next);
    onOpenChange?.(next);
  };

  const handleSign = async () => {
    try {
      const values = await form.validateFields();
      await onSign(values.signerName.trim());
      message.success("License agreement signed");
      if (!mandatory) setOpen(false);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || "Could not save signature");
    }
  };

  const footer = signed ? (
    <Button type="primary" onClick={() => setOpen(false)}>
      Close
    </Button>
  ) : (
    <Button type="primary" loading={signing} onClick={handleSign}>
      Sign agreement
    </Button>
  );

  return (
    <>
      {showTriggerButton && signed ? (
        <Button
          type="primary"
          ghost
          className="vc-license-agreement-btn"
          onClick={() => setOpen(true)}
        >
          {contractConfig?.title || "License Agreement"}
        </Button>
      ) : null}
      <Modal
        title={contractConfig?.title || "License Agreement"}
        visible={open}
        onCancel={() => {
          if (!mandatory) setOpen(false);
        }}
        footer={footer}
        cancelButtonProps={
          mandatory && !signed ? { style: { display: "none" } } : undefined
        }
        closable={!mandatory || signed}
        maskClosable={!mandatory}
        keyboard={!mandatory}
        destroyOnClose={!mandatory}
        wrapClassName="vc-lead-modal-wrap"
        bodyStyle={{ paddingTop: 8, maxHeight: "65vh", overflowY: "auto" }}
        width={720}
      >
        {mandatory && !signed ? (
          <Paragraph type="secondary" style={{ marginTop: 0 }}>
            You must sign this agreement with your full legal name before you
            can view YouTube revenue estimates for this project.
          </Paragraph>
        ) : null}
        <AgreementBody />
        {!signed ? (
          <>
            <Paragraph style={{ marginTop: 16, marginBottom: 8 }}>
              <Text strong>Digital signature</Text>
              <br />
              <Text type="secondary">
                Type your full legal name below. This constitutes your
                electronic signature on this agreement.
              </Text>
            </Paragraph>
            <Form form={form} layout="vertical" requiredMark={false}>
              <Form.Item
                name="signerName"
                label="Full legal name"
                rules={[
                  { required: true, message: "Enter your full legal name" },
                  {
                    min: 2,
                    message: "Name must be at least 2 characters",
                  },
                  {
                    whitespace: true,
                    message: "Enter your full legal name",
                  },
                ]}
              >
                <Input
                  placeholder="As it appears on official documents"
                  autoComplete="name"
                  disabled={signing}
                />
              </Form.Item>
              <Form.Item
                name="accepted"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, v) =>
                      v
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(
                              "You must accept the agreement to continue"
                            )
                          ),
                  },
                ]}
              >
                <Checkbox disabled={signing}>
                  I have read this license agreement and agree to its terms.
                </Checkbox>
              </Form.Item>
            </Form>
          </>
        ) : (
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            You signed this agreement electronically. Use Close to return to the
            page.
          </Paragraph>
        )}
      </Modal>
    </>
  );
}
